import * as React from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import { PageLayout } from '../components/layout';
import { ActionButton, TabButton, ActionSpacer } from '../components/common';
import { SiteDetailForm } from '../components/site/SiteDetailForm';
import {
  useCustomerSitesById,
  useCreateCustomerSites,
  useUpdateCustomerSites,
  useCustomer,
} from '../hooks';
import { siteFormSchema, SiteFormData } from '../schemas';
import {
  CustomerSitesCreateDTO,
  SelfProductionPlantsCreateDTO,
  SelfProductionPlantsReadDTO,
} from '../generated/api-client';
import {
  SelfProductionPlantsGrid,
  type SelfProductionPlantsGridProps,
} from '../components/selfProduction/SelfProductionPlantsGrid';
import { CrudDataGridHandle } from '../components/CrudDataGrid';
import { useQueryClient } from 'react-query';
import { apiClient } from '../services/apiClient';
import { formatDateForServer } from '../utils/date.utils';
import SaveIcon from '@mui/icons-material/Save';
import dayjs from 'dayjs';

export const SiteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewSite = id === 'new';
  const customerIdFromQuery = searchParams.get('customer_id') || '';
  const [activeTab, setActiveTab] = React.useState<'details' | 'selfProduction'>('details');
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] = React.useState(false);
  const selfProductionGridHandleRef = React.useRef<CrudDataGridHandle>(null);
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const [siteImage, setSiteImage] = React.useState<string | null>(null);

  const siteFormMethods = useForm<SiteFormData>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: {
      customer_id: customerIdFromQuery,
      address: '',
      iso_50001_certified_site: null,
      covered_area: null,
      telecom_network_presence: null,
      cogeneration_availability: null,
      excluded_areas: '',
      norm_unicei_16247_1: true,
      norm_unicei_16247_2: false,
      norm_unicei_16247_3: false,
      norm_unicei_16247_4: false,
      notes: '',
      property: '',
      type: '',
      availability_until: null,
      construction_year: null,
      renovation_year: null,
      energy_performance_certificate: false,
      efficiency_class: '',
      building_envelope_walls_floors: '',
      building_envelope_insulation: '',
      building_envelope_transmittance: '',
      fixtures_glass: '',
      fixtures_frame: '',
      fixtures_transmittance: '',
      fixtures_shading: '',
      img: null,
      climatic_zone: undefined,
      start_summer: undefined,
      start_winter: undefined,
      has_podp: 0,
      has_podi: 0,
      has_prod: 0,
      has_pdr: 0,
    },
    mode: 'onChange',
  });

  const { reset } = siteFormMethods;

  const { data: site, isLoading: isLoadingSite } = useCustomerSitesById(isNewSite ? '' : id || '');
  const customerIdForFetch = isNewSite ? customerIdFromQuery : site?.customer_id || '';
  const { data: customer } = useCustomer(customerIdForFetch);

  const siteSelfProductionGridProps = React.useMemo<SelfProductionPlantsGridProps<
    SelfProductionPlantsReadDTO,
    SelfProductionPlantsCreateDTO
  > | null>(() => {
    if (!id || id === 'new') {
      return null;
    }
    return {
      queryKey: `site-self-production-plants-${id}`,
      listFn: () =>
        apiClient.search_by_site_id_self_production_plants_search_by_site_id__site_id__get(id),
      createFn: data =>
        apiClient.create_item_self_production_plants__post(
          new SelfProductionPlantsCreateDTO({
            ...data,
            site_id: id,
          })
        ),
      updateFn: (plantId, data) =>
        apiClient.update_item_self_production_plants__item_id__put(
          plantId,
          new SelfProductionPlantsCreateDTO({
            ...data,
            site_id: id,
          })
        ),
      deleteFn: plantId => apiClient.delete_item_self_production_plants__item_id__delete(plantId),
      getId: row => row.id ?? '',
      getCreateDto: row =>
        new SelfProductionPlantsCreateDTO({
          site_id: id,
          type: row.type ?? undefined,
          power_source: row.power_source ?? undefined,
          in_service_year: formatDateForServer(row.in_service_year),
          electrical_power_kw: row.electrical_power_kw
            ? Number(row.electrical_power_kw)
            : undefined,
          thermal_power_kw: row.thermal_power_kw ? Number(row.thermal_power_kw) : undefined,
          cooling_power_kw: row.cooling_power_kw ? Number(row.cooling_power_kw) : undefined,
        }),
    };
  }, [id]);

  React.useEffect(() => {
    if (
      !isNewSite &&
      id &&
      (location.pathname.includes('/immobile') || location.pathname.includes('/confini'))
    ) {
      navigate(`/sites/${id}`, { replace: true });
    }
  }, [location.pathname, isNewSite, navigate, id]);

  React.useEffect(() => {
    if (site && !isNewSite) {
      const formData: SiteFormData = {
        customer_id: site.customer_id || '',
        address: site.address || '',
        iso_50001_certified_site: site.iso_50001_certified_site ?? null,
        covered_area: site.covered_area ?? null,
        telecom_network_presence: site.telecom_network_presence ?? null,
        cogeneration_availability: site.cogeneration_availability ?? null,
        excluded_areas: (site as any).excluded_areas || '',
        norm_unicei_16247_1: (site as any).norm_unicei_16247_1 ?? true,
        norm_unicei_16247_2: (site as any).norm_unicei_16247_2 ?? false,
        norm_unicei_16247_3: (site as any).norm_unicei_16247_3 ?? false,
        norm_unicei_16247_4: (site as any).norm_unicei_16247_4 ?? false,
        notes: (site as any).notes || '',
        property: (site as any).property || '',
        type: (site as any).type || '',
        availability_until: (site as any).availability_until
          ? new Date((site as any).availability_until)
          : null,
        construction_year: (site as any).construction_year
          ? new Date((site as any).construction_year)
          : null,
        renovation_year: (site as any).renovation_year
          ? new Date((site as any).renovation_year)
          : null,
        energy_performance_certificate: (site as any).energy_performance_certificate ?? false,
        efficiency_class: (site as any).efficiency_class || '',
        building_envelope_walls_floors: (site as any).building_envelope_walls_floors || '',
        building_envelope_insulation: (site as any).building_envelope_insulation || '',
        building_envelope_transmittance: (site as any).building_envelope_transmittance || '',
        fixtures_glass: (site as any).fixtures_glass || '',
        fixtures_frame: (site as any).fixtures_frame || '',
        fixtures_transmittance: (site as any).fixtures_transmittance || '',
        fixtures_shading: (site as any).fixtures_shading || '',
        img: (site as any).img || null,
        climatic_zone:
          (site as any).climatic_zone && (site as any).climatic_zone.trim() !== ''
            ? (site as any).climatic_zone.trim()
            : undefined,
        start_summer:
          (site as any).start_summer && (site as any).start_summer.trim() !== ''
            ? (site as any).start_summer.trim()
            : undefined,
        start_winter:
          (site as any).start_winter && (site as any).start_winter.trim() !== ''
            ? (site as any).start_winter.trim()
            : undefined,
        has_podp: (site as any).has_podp ?? 0,
        has_podi: (site as any).has_podi ?? 0,
        has_prod: (site as any).has_prod ?? 0,
        has_pdr: (site as any).has_pdr ?? 0,
      };
      reset(formData, { keepDefaultValues: false, keepDirty: false });
      setSiteImage((site as any).img || null);
    } else if (isNewSite) {
      reset(
        {
          customer_id: customerIdFromQuery,
          address: '',
          iso_50001_certified_site: null,
          covered_area: null,
          telecom_network_presence: null,
          cogeneration_availability: null,
          excluded_areas: '',
          norm_unicei_16247_1: true,
          norm_unicei_16247_2: false,
          norm_unicei_16247_3: false,
          norm_unicei_16247_4: false,
          notes: '',
          property: '',
          type: '',
          availability_until: null,
          construction_year: null,
          renovation_year: null,
          energy_performance_certificate: false,
          efficiency_class: '',
          building_envelope_walls_floors: '',
          building_envelope_insulation: '',
          building_envelope_transmittance: '',
          fixtures_glass: '',
          fixtures_frame: '',
          fixtures_transmittance: '',
          fixtures_shading: '',
          img: null,
          climatic_zone: undefined,
          start_summer: undefined,
          start_winter: undefined,
          has_podp: 0,
          has_podi: 0,
          has_prod: 0,
          has_pdr: 0,
        },
        { keepDefaultValues: false, keepDirty: false }
      );
    }
  }, [site, isNewSite, customerIdFromQuery, reset]);

  const updateSiteMutation = useUpdateCustomerSites();
  const createSiteMutation = useCreateCustomerSites();

  const isSaving = updateSiteMutation.isLoading || createSiteMutation.isLoading;
  const overallLoading = !isNewSite && isLoadingSite;

  const hasUnsavedChanges = siteFormMethods.formState.isDirty;
  const addressValue = siteFormMethods.watch('address');

  const handleTabChange = (tab: 'details' | 'selfProduction') => {
    setActiveTab(tab);
  };

  const handleSubmitForm = (data: SiteFormData, force = false) => {
    if (!data.customer_id) {
      toast.error('Seleziona un customer prima di salvare il sito');
      return;
    }

    // Convert dates to YYYY-MM-DD format strings for the server
    // The server expects date strings without time component
    const formatDateForServer = (date: Date | null | undefined): string | undefined => {
      if (!date) return undefined;
      return dayjs(date).format('YYYY-MM-DD');
    };

    // Create payload with date strings instead of Date objects
    // This prevents axios from serializing them as ISO datetime strings
    const payload = {
      ...data,
      availability_until: formatDateForServer(data.availability_until),
      construction_year: formatDateForServer(data.construction_year),
      renovation_year: formatDateForServer(data.renovation_year),
    } as any;

    if (isNewSite) {
      createSiteMutation.mutate(payload, {
        onSuccess: newSite => {
          toast.success('Sito creato con successo');
          navigate(`/sites/${newSite.id}`, { replace: true });
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Errore durante la creazione del sito');
        },
      });
    } else {
      if (!id) {
        toast.error('Identificativo del sito mancante');
        return;
      }
      updateSiteMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            toast.success('Sito aggiornato con successo');
            const savedValues = siteFormMethods.getValues();
            reset(savedValues, { keepDefaultValues: false, keepDirty: false });
          },
          onError: (error: any) => {
            toast.error(error?.message || "Errore durante l'aggiornamento del sito");
          },
        }
      );
    }
  };

  const handleSave = async () => {
    const isValid = await siteFormMethods.trigger();
    if (!isValid) {
      const errors = siteFormMethods.formState.errors;
      const hasErrors = Object.keys(errors).length > 0;

      if (hasErrors) {
        const hasOnlyRequiredErrors = Object.values(errors).every(error => {
          if (!error) return false;
          const message = error.message;
          if (typeof message === 'string') {
            return message.includes('obbligatorio') || message.includes('obbligatoria');
          }
          if (Array.isArray(message)) {
            return message.every(
              m =>
                typeof m === 'string' && (m.includes('obbligatorio') || m.includes('obbligatoria'))
            );
          }
          return false;
        });

        if (hasOnlyRequiredErrors) {
          setShowConfirmSaveDialog(true);
        }
        return;
      }
    }

    siteFormMethods.handleSubmit(data => handleSubmitForm(data, false))();
  };

  const handleConfirmSave = () => {
    const values = siteFormMethods.getValues();
    handleSubmitForm(values, true);
    setShowConfirmSaveDialog(false);
  };

  const handleCancelSave = () => {
    setShowConfirmSaveDialog(false);
  };

  if (overallLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 6,
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress size={80} />
      </Box>
    );
  }

  if (!site && !isNewSite) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Sito non trovato</Typography>
      </Box>
    );
  }

  return (
    <PageLayout
      header={
        <>
          <LocationOnIcon fontSize="large" />
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<Typography variant="h5">/</Typography>}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: '1rem' }}
          >
            {customer && (
              <MuiLink
                component="button"
                variant="body1"
                onClick={() => navigate(`/customers/${customer.id}/siti`)}
                sx={{
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  font: 'inherit',
                }}
              >
                <Typography variant="h5">{customer.company_name || 'N/A'}</Typography>
              </MuiLink>
            )}
            {!customer && !isNewSite && <Typography variant="h5">Customer</Typography>}
            {isNewSite && <Typography variant="h5">Nuovo Sito</Typography>}
            {!isNewSite && site && (
              <Typography variant="h5" color="text.primary">
                {site.address || 'N/A'}
              </Typography>
            )}
          </Breadcrumbs>
        </>
      }
      actions={
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TabButton
              icon={<LocationOnIcon fontSize="small" />}
              label="Dettagli sito"
              active={activeTab === 'details'}
              onClick={() => handleTabChange('details')}
            />
            <TabButton
              icon={<EnergySavingsLeafIcon fontSize="small" />}
              label="Autoproduzione"
              active={activeTab === 'selfProduction'}
              onClick={() => handleTabChange('selfProduction')}
              disabled={isNewSite}
            />
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'divider' }} />

          {activeTab === 'details' ? (
            <ActionButton
              icon={<SaveIcon />}
              tooltip={hasUnsavedChanges ? 'Salva modifiche' : 'Nessuna modifica da salvare'}
              onClick={handleSave}
              disabled={!hasUnsavedChanges && !isNewSite}
              loading={isSaving}
            />
          ) : (
            <ActionButton
              icon={<AddIcon />}
              tooltip={
                isNewSite ? 'Salva il sito per aggiungere gli impianti' : 'Aggiungi impianto'
              }
              onClick={() => selfProductionGridHandleRef.current?.addRow()}
              disabled={isNewSite || !siteSelfProductionGridProps}
            />
          )}
        </>
      }
    >
      <Box sx={{ p: 2 }}>
        {activeTab === 'details' && (
          <FormProvider {...siteFormMethods}>
            <Box
              component="form"
              onSubmit={siteFormMethods.handleSubmit(handleSubmitForm as any)}
              noValidate
            >
              <SiteDetailForm
                siteId={!isNewSite ? id : undefined}
                mapContainerRef={mapContainerRef}
                img={siteImage}
                onChangeImg={setSiteImage}
              />
            </Box>
          </FormProvider>
        )}

        {activeTab === 'selfProduction' &&
          (isNewSite ? (
            <Typography sx={{ mt: 1 }}>
              Salva il sito per gestire gli impianti di autoproduzione.
            </Typography>
          ) : (
            siteSelfProductionGridProps && (
              <SelfProductionPlantsGrid
                ref={selfProductionGridHandleRef}
                {...siteSelfProductionGridProps}
                height="calc(100vh - 190px)"
              />
            )
          ))}
        <ActionSpacer></ActionSpacer>
      </Box>

      <Dialog open={showConfirmSaveDialog} onClose={handleCancelSave}>
        <DialogTitle>Conferma salvataggio dati</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ci sono alcuni campi obbligatori non compilati, vuoi procedere ugualmente al
            salvataggio?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box sx={{ pb: 2, pr: 2 }}>
            <Button onClick={handleCancelSave} color="inherit">
              Annulla
            </Button>
            <Button sx={{ ml: 2 }} onClick={handleConfirmSave} color="primary" variant="contained">
              Salva comunque
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};
